/**
 * Complete-task dialog — capture outcome + optional next action.
 */
import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../lib/api';

export function CompleteTaskModal({ task, onClose, onDone }) {
  const [outcome, setOutcome] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [nextDate, setNextDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setOutcome('');
    setNextAction('');
    setNextDate('');
  }, [task?.id]);

  if (!task) return null;

  const submit = async () => {
    setSaving(true);
    try {
      await api.completeTask(task.id, {
        outcome,
        next_action: nextAction || null,
        next_action_date: nextAction && nextDate ? new Date(`${nextDate}T09:00:00`).toISOString() : null,
        create_next_task: !!(nextAction && nextDate),
      });
      toast.success('Task completed');
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.message || 'Failed to complete task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!task} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="complete-task-modal">
        <DialogHeader><DialogTitle>Complete: {task.title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Textarea placeholder="Outcome (what happened?)" value={outcome} onChange={(e) => setOutcome(e.target.value)} data-testid="complete-task-outcome-input" />
          <Input placeholder="Next action (optional)" value={nextAction} onChange={(e) => setNextAction(e.target.value)} data-testid="complete-task-next-action-input" />
          {nextAction && (
            <Input type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} data-testid="complete-task-next-date-input" />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="complete-task-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="complete-task-submit-btn">{saving ? 'Saving…' : 'Mark Complete'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
